import { DebinResult } from '../types/IDebin';
import { FixedTermStatus } from '../types/IFixedTermItem';
import { IFixedTermTexts } from '../types/IFixedTermTexts';

export const APP_TITLE = 'Plazo Fijo Inclusión';

/** Login */
export const LOGIN_DEFAULT_ERROR_MESSAGE = 'Por favor intentá más tarde';
export const LOGIN_TITLE = 'Ingreso';
export const LOGIN_NO_CLIENT = 'El correo ingresado no corresponde a un usuario registrado. Registrate desde la opción <b>Registro</b>';
export const SESSION_KEY = 'token';

export const TERMINOS_Y_CONDICIONES_URL = '/CDN/Get/plazo_fijo_web_terminos_y_condiciones';
export const TERMINOS_Y_CONDICIONES = 'términos y condiciones';

export const REGISTRO_NUMEROTRAMITE_INFO = 'El número de trámite de tu DNI se encuentra en la parte frontal debajo del título "Trámite N°". Consta de 11 dígitos.';

/**Información sobre términos que debe aceptar el cliente.*/
export const PEP_INFO = 'Son personas que se desempeñan en cargos políticos o tienen vínculos familiares o de amistad con alguien que sí lo hace.';
export const SOI_INFO = 'Son personas que, por su actividad, deben informar operaciones ante la Unidad de la Información Financiera (UIF).';
export const FATCA_INFO = 'Son personas sujetos fiscales de Estados Unidos que deben declarar sus activos financieros en el extranjero al IRS bajo la Ley FATCA.';

/**Menú principal*/
export const MENU_ITEM_LIST = 'Tus plazos fijos';
export const MENU_ITEM_NEW = 'Nuevo plazo fijo inclusión';

/**Tabla de plazos fijos */
export const FIXED_TERM_STATUS_MAPPING: { [index in FixedTermStatus]: IFixedTermTexts } = {
  [FixedTermStatus.EN_CURSO]: { status: 'Plazo Fijo Activo', tooltip: 'Tu Plazo Fijo se encuentra dado de alta y generando intereses hasta la fecha de vencimiento.' },
  [FixedTermStatus.RECHAZADO]: { status: 'Plazo Fijo Anulado', tooltip: 'Los fondos fueron enviados mediante un Credin a tu cuenta de otro banco.' },
  [FixedTermStatus.DEBIN_SIN_AUTORIZAR]: { status: 'Debin Pendiente', tooltip: 'La orden de Debin fue enviada a tu banco. Ingresá al Home Banking y autorizá la operación.' },
  [FixedTermStatus.ERROR_PLAZOFIJO]: {
    status: 'Plazo Fijo Pendiente',
    tooltip: 'Confirmá que aceptaste el Debin. De lo contrario, el plazo fijo se dará de alta de forma automática después de las 16hs.',
  },
  [FixedTermStatus.PLAZOFIJO_CADUCADO]: { status: 'Plazo Fijo Anulado', tooltip: 'Los fondos fueron enviados mediante un Credin a tu cuenta de otro banco.' },
  [FixedTermStatus.CREDIN_EN_CURSO]: { status: 'Credin en curso', tooltip: '' },
  [FixedTermStatus.CREDIN_ACREDITADO]: { status: 'Plazo Fijo Acreditado', tooltip: 'Los fondos y los intereses fueron enviados mediante un Credin a tu cuenta de otro banco.' },
  [FixedTermStatus.DEBIN_VENCIDO]: { status: 'Debin Vencido', tooltip: 'El Debin expiró tras cumplirse el plazo de 60 minutos para su aprobación.' },
  [FixedTermStatus.DEBIN_RECHAZADO]: { status: 'Debin Rechazado', tooltip: 'El Debin fue rechazado en el Home Banking de tu banco.' },
};

export const fixedTermStatus = (status: string): string => {
  const textsContainer = FIXED_TERM_STATUS_MAPPING[status as FixedTermStatus];

  if (!textsContainer?.status) {
    return status;
  } else {
    return textsContainer.status;
  }
};

export const fixedTermTooltip = (status: string | undefined): string => {
  const textsContainer = FIXED_TERM_STATUS_MAPPING[status as FixedTermStatus];

  return textsContainer?.tooltip ?? '';
};

/**Tabla de colores para estado */
const FIXED_TERM_COLOR_CURRENT = 'primary.0';
const FIXED_TERM_COLOR_PENDING = 'tertiary.0';
const FIXED_TERM_COLOR_CLOSED = 'secondary.1';

export const FIXED_TERM_STATUS_COLOR_MAPPING: { [index in FixedTermStatus]: string } = {
  [FixedTermStatus.EN_CURSO]: FIXED_TERM_COLOR_CURRENT,
  [FixedTermStatus.RECHAZADO]: FIXED_TERM_COLOR_CLOSED,
  [FixedTermStatus.DEBIN_SIN_AUTORIZAR]: FIXED_TERM_COLOR_PENDING,
  [FixedTermStatus.ERROR_PLAZOFIJO]: FIXED_TERM_COLOR_PENDING,
  [FixedTermStatus.PLAZOFIJO_CADUCADO]: FIXED_TERM_COLOR_CLOSED,
  [FixedTermStatus.CREDIN_EN_CURSO]: FIXED_TERM_COLOR_CURRENT,
  [FixedTermStatus.CREDIN_ACREDITADO]: FIXED_TERM_COLOR_CLOSED,
  [FixedTermStatus.DEBIN_VENCIDO]: FIXED_TERM_COLOR_CLOSED,
  [FixedTermStatus.DEBIN_RECHAZADO]: FIXED_TERM_COLOR_CLOSED,
};

export const fixedTermStatusColor = (status: string): string => {
  const _status = FIXED_TERM_STATUS_COLOR_MAPPING[status as FixedTermStatus];

  if (!_status) {
    return FIXED_TERM_COLOR_CLOSED;
  } else {
    return _status;
  }
};

export const TABLE_NO_DATA_CELL = ' ';

export const TABLE_EMPTY_FIXED_TERMS = `No tenés plazos fijos en este momento. Podes crear uno en <b>${MENU_ITEM_NEW}</b>.`;
export const TABLE_UNCONFIRMED_FIXED_TERMS_WARNING = 'Luego de aprobar el DEBIN debes confirmar el alta de tu plazo fijo en <b> "Ya acepté el DEBIN..." </b>';
export const TABLE_ERROR_FIXED_TERMS_WARNING = 'Tenés plazos fijos pendientes de confirmar mediante la opción <b>"Constituir PF"</b> ';

/**Restricción operación en día hábil */
export const TIME_RESTRICTION_START = 10;
export const TIME_RESTRICTION_END = 15;
export const TIME_RESTRICTION_MESSAGE = `Podrás dar de alta el plazo fijo los <b> días hábiles de ${TIME_RESTRICTION_START} a ${TIME_RESTRICTION_END} horas</b>.`;

/** Textos que se muestran en el Simulador. */
export const SIMULATOR_INFO_TEXT = `El Plazo Fijo podrá solicitarse en días hábiles de ${TIME_RESTRICTION_START} a ${TIME_RESTRICTION_END} horas.`;
export const SIMULATOR_WARNING_TEXT1 =
  'No se podrán adicionar nuevos fondos al plazo fijo, mientras el mismo se encuentre vigente. Los plazos fijos constituidos no podrán ser cancelados hasta su fecha de vencimiento (excepto en los casos de imposiciones a plazo con opción de Cancelación Anticipada).';
export const SIMULATOR_WARNING_TEXT2 =
  'La totalidad de los depósitos efectuados en esta Institución se encuentran garantizados por la Provincia de Buenos Aires. Entidad adherida al Seguro de Garantía de depósitos Ley 24.485, Decreto 540/95 y Com. "A" 2337 y sus modificaciones y complementarias del B.C.R.A.';
/** Textos que se muestran antes de confirmar envio de Debin. */
export const DEBIN_INFO_TEXT = 'Sólo se permiten CBUs de otros bancos asociados a cuentas en pesos a tu nombre';
export const DEBIN_WARNING_TEXT1 = 'Se enviará un DEBIN a la cuenta elegida.';
export const DEBIN_WARNING_TEXT2 =
  'Aceptá el DEBIN en tu otro banco dentro de los próximos 60 minutos.  Luego, recordá volver a nuestro sitio para confirmar la acción y confirmar el Plazo Fijo.';
export const DEBIN_WARNING_TEXT3 = '¿Desea continuar?';
export const ALIAS_CHECK_ERROR = 'Sólo está permitido ingresar alias de cuentas de otro Banco';
/** Textos que se muestran después de confirmar envio de Debin*/
export const DEBIN_PENDING_WARNING_TEXT1 = 'El DEBIN ha sido enviado al CBU seleccionado.';
export const DEBIN_PENDING_WARNING_TEXT2 = 'Aceptá el DEBIN en tu otro banco dentro de los próximos 60 minutos.';
export const DEBIN_PENDING_WARNING_TEXT3 = 'Luego, recordá volver a nuestro sitio para confirmar la acción y confirmar el Plazo Fijo.';

export const DEBIN_LOADING_MESSAGES = [
  'Enviando detalles de plazo fijo',
  'Calculando intereses',
  'Buscando acreditación de DEBIN',
  'Acreditación encontrada',
  'Iniciando contratación de plazo fijo',
  'Plazo fijo contratado exitosamente',
  'Enviando certificado por correo electrónico',
];

export const DEBIN_SUCCESSFUL_MESSAGE = 'Plazo fijo dado de alta';
export const DEBIN_CONFIRM_ERROR = 'No se pudo dar de alta el plazo fijo';

/** Mapeo que permite convertir de estado de Debin a estado de plazo fijo */
export const DEBIN_STATUS_TO_FIXEDTERM_STATUS: { [index in DebinResult]: FixedTermStatus } = {
  [DebinResult.INICIAL]: FixedTermStatus.DEBIN_SIN_AUTORIZAR,
  [DebinResult.ERROR]: FixedTermStatus.ERROR_PLAZOFIJO,
  [DebinResult.COMPLETED]: FixedTermStatus.EN_CURSO,
};

export const CBU_CHECK_MSG = 'Revisá el Alias/CBU, ya que de esta cuenta se debitarán los fondos para el PF';

export const DEBIN_REJECTED_MESSAGE = 'No se pudo crear el DEBIN. Por favor, dirígite a una sucursal';

export const DEBIN_GENERAL_ERROR = 'No se pudo crear el DEBIN. Por favor, vuelve a intentar más tarde';

export const LIST_GENERAL_ERROR = 'No se pudieron obtener tus plazos fijos. Por favor intenta de nuevo';

export const ERROR_CODE_TEXT = 'código de error';
